import React, { useEffect, useState } from "react"

interface TocItem {
  id: string
  text: string
  level: number
}

interface DocsTocProps {
  /** The article's own scroll pane; used as the querying + observer root. */
  scrollRef: React.RefObject<HTMLElement | null>
  /** Changes when the page content changes, so the TOC re-reads the DOM. */
  contentKey: string
}

/**
 * "On this page" outline built from the rendered article headings, with an
 * IntersectionObserver scrollspy marking the current section.
 */
const DocsToc: React.FC<DocsTocProps> = ({ scrollRef, contentKey }) => {
  const [items, setItems] = useState<TocItem[]>([])
  const [activeId, setActiveId] = useState<string>("")

  useEffect(() => {
    const root = scrollRef.current
    if (!root) return

    const headings = Array.from(
      root.querySelectorAll<HTMLElement>(".docs-markdown h2, .docs-markdown h3"),
    ).filter((heading) => heading.id)

    setItems(
      headings.map((heading) => ({
        id: heading.id,
        text: heading.textContent ?? "",
        level: heading.tagName === "H2" ? 2 : 3,
      })),
    )
    setActiveId(headings[0]?.id ?? "")

    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          )
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { root, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    )

    headings.forEach((heading) => observer.observe(heading))
    return () => observer.disconnect()
  }, [scrollRef, contentKey])

  const handleClick = (event: React.MouseEvent, id: string): void => {
    event.preventDefault()
    const target = document.getElementById(id)
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" })
      setActiveId(id)
      window.history.replaceState(null, "", `#${id}`)
    }
  }

  if (items.length === 0) return null

  return (
    <nav className="docs-toc" aria-label="On this page">
      <p className="docs-toc__title">On this page</p>
      <ul className="docs-toc__list">
        {items.map((item) => (
          <li
            key={item.id}
            className={`docs-toc__item docs-toc__item--h${item.level}`}
          >
            <a
              href={`#${item.id}`}
              className={`docs-toc__link${
                activeId === item.id ? " docs-toc__link--active" : ""
              }`}
              onClick={(event) => handleClick(event, item.id)}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default DocsToc
